import { useCallback, useMemo, useState } from 'react';
import { ANSWER_SLA_DAYS } from '../entities/supportRequests/model/constants';
import { useDebouncedValue } from './useDebouncedValue';
import { daysSince } from '../utils/format';
import type {
    RequestFilters,
    RequestsSummary,
    SortKey,
    SortState,
    StatusCounts,
    StatusTab,
    SupportRequest,
} from '../entities/supportRequests/model/types';
import { useGetSupportRequestsPageQuery } from '../entities/supportRequests/api/supportRequestApi';
import { DEFAULT_PAGE_SIZE } from '../shared/config/pagination';

const EMPTY_FILTERS: RequestFilters = { query: '', theme: '', priority: '', status: 'all' };

const SORT_FIELD: Record<SortKey, string | null> = {
    date: 'created_at',
    status: 'status',
    priority: 'criticality',
    id: null,
};

export const isOpen = (r: SupportRequest): boolean => r.status === 'new' || r.status === 'work';

export const waitingDays = (r: SupportRequest): number =>
    isOpen(r) && r.replies.length === 0 ? daysSince(r.createdAt) : 0;

export const isOverdue = (r: SupportRequest): boolean => waitingDays(r) > ANSWER_SLA_DAYS;

export function useSupportRequests() {
    const [filters, setFilters] = useState<RequestFilters>(EMPTY_FILTERS);
    const [sort, setSort] = useState<SortState>({ key: 'date', dir: -1 });
    const [page, setPage] = useState(1);

    const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

    const search = useDebouncedValue(filters.query, 400);

    const sortField = SORT_FIELD[sort.key];

    const { data, isLoading, isFetching, isError, refetch } = useGetSupportRequestsPageQuery({
        pageNum: page,
        pageSize: perPage,
        query: search,
        sortBy: sortField ?? undefined,
        sortOrder: sort.dir === 1 ? 'asc' : 'desc',
    });

    const items = useMemo(() => data?.items ?? [], [data]);
    const total = data?.total ?? 0;
    const pageCount = Math.max(1, data?.pages ?? 1);

    const withoutStatus = useMemo(
        () =>
            items.filter((r) => {
                if (filters.theme && r.theme !== filters.theme) return false;
                if (filters.priority && r.priority !== filters.priority) return false;
                return true;
            }),
        [items, filters.theme, filters.priority],
    );

    const counts: StatusCounts = useMemo(
        () => ({
            all: withoutStatus.length,
            new: withoutStatus.filter((r) => r.status === 'new').length,
            work: withoutStatus.filter((r) => r.status === 'work').length,
            done: withoutStatus.filter((r) => r.status === 'done').length,
            rej: withoutStatus.filter((r) => r.status === 'rej').length,
        }),
        [withoutStatus],
    );

    const rows = useMemo(
        () =>
            filters.status === 'all'
                ? withoutStatus
                : withoutStatus.filter((r) => r.status === filters.status),
        [withoutStatus, filters.status],
    );

    const summary: RequestsSummary = useMemo(
        () => ({
            total,
            fresh: items.filter((r) => r.status === 'new').length,
            overdue: items.filter(isOverdue).length,
            doneLastMonth: items.filter((r) => r.status === 'done' && daysSince(r.createdAt) <= 30).length,
            inProgress: items.filter(isOpen).length,
        }),
        [items, total],
    );

    const patchFilters = useCallback((patch: Partial<RequestFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
        setPage(1);
    }, []);

    const setStatusTab = useCallback((status: StatusTab) => patchFilters({ status }), [patchFilters]);

    const resetFilters = useCallback(() => {
        setFilters(EMPTY_FILTERS);
        setPage(1);
    }, []);

    /** Повторный клик по той же колонке переворачивает порядок. */
    const toggleSort = useCallback((key: SortKey) => {
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: key === 'date' ? -1 : 1 },
        );
        setPage(1);
    }, []);

    const changePerPage = useCallback((value: number) => {
        setPerPage(value);
        setPage(1);
    }, []);

    const hasFilters =
        filters.query !== '' || filters.theme !== '' || filters.priority !== '' || filters.status !== 'all';

    return {
        requests: items,
        rows,
        total,
        counts,
        summary,
        filters,
        hasFilters,
        patchFilters,
        setStatusTab,
        resetFilters,
        sort,
        toggleSort,
        page,
        pageCount,
        setPage,
        perPage,
        changePerPage,
        from: (page - 1) * perPage,
        isLoading,
        isFetching,
        isError,
        refetch,
    };
}